// src/components/EntryModal.js
import React, { useContext, useState, useEffect } from "react";
import { LanguageContext } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../services/api";

function EntryModal({ isOpen, onClose }) {
  const { lang, toggleLanguage } = useContext(LanguageContext);
  const navigate = useNavigate();

  // mode: "choice" | "login" | "register"
  const [mode, setMode] = useState("choice");

  // LOGIN FORM STATE
  const [loginIdentifier, setLoginIdentifier] = useState(""); // email or phone
  const [loginName, setLoginName] = useState("");

  // REGISTER FORM STATE
  const [regPhone, setRegPhone] = useState("");
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");

    // PHONE VALIDATION (touched)
  const [loginIdTouched, setLoginIdTouched] = useState(false);
  const [regPhoneTouched, setRegPhoneTouched] = useState(false);

  // UI ERROR STATE
const [uiError, setUiError] = useState("");


  const isEmail = (v = "") => v.includes("@");

  const formatLbLocal = (digits) => {
    // expects up to 8 digits => "DD DDD DDD"
    const d = digits.slice(0, 8);
    const p1 = d.slice(0, 2);
    const p2 = d.slice(2, 5);
    const p3 = d.slice(5, 8);
    return [p1, p2, p3].filter(Boolean).join(" ");
  };

  const formatLbPlus961 = (digitsAfter961) => {
    // expects up to 8 digits after +961 => "+961 DD DDD DDD"
    const d = digitsAfter961.slice(0, 8);
    const p1 = d.slice(0, 2);
    const p2 = d.slice(2, 5);
    const p3 = d.slice(5, 8);
    const parts = [p1, p2, p3].filter(Boolean).join(" ");
    return parts ? `+961 ${parts}` : `+961`;
  };

  const formatPhoneInput = (raw) => {
    const v = (raw || "").trim();

    // +961 flow (force formatting)
    if (v.startsWith("+961")) {
      const after = v.replace(/\s/g, "").slice(4); // remove spaces, remove +961
      const digits = after.replace(/\D/g, "");
      return formatLbPlus961(digits);
    }

    // local LB flow (0/7/8) (force formatting)
    // NOTE: we look at first non-space char
    const firstChar = v.replace(/\s/g, "").charAt(0);
    if (firstChar === "0" || firstChar === "7" || firstChar === "8") {
      const digits = v.replace(/\D/g, "").slice(0, 8);
      return formatLbLocal(digits);
    }

    // international / other -> do not change
    return raw;
  };

  const validatePhone = (value) => {
    const v = (value || "").trim();

    if (v.startsWith("+961")) {
      const all = v.replace(/\s/g, "");
      const after = all.slice(4); // after +961
      const digits = after.replace(/\D/g, "");
      if (digits.length !== 8) {
        return "Phone must be +961 followed by 8 digits.";
      }
      return null;
    }

    const compact = v.replace(/\s/g, "");
    const firstChar = compact.charAt(0);

    if (firstChar === "0" || firstChar === "7" || firstChar === "8") {
      const digits = compact.replace(/\D/g, "");
      if (digits.length !== 8) {
        return "Phone must be exactly 8 digits.";
      }
      return null;
    }

    // international/other: no forced validation (as requested)
    return null;
  };


  useEffect(() => {
  if (isOpen && localStorage.getItem("esill-open-login")) {
    setMode("login");
    localStorage.removeItem("esill-open-login");
  }
}, [isOpen]);

  if (!isOpen) return null;


  const handleClose = () => {
    setUiError("");
    setMode("choice");
    onClose();
  };

  const handleLoginSubmit = async (e) => {
  e.preventDefault();
    setLoginIdTouched(true);

  if (!isEmail(loginIdentifier)) {
    const err = validatePhone(loginIdentifier);
    if (err) return;
  }

  if (!loginIdentifier || !loginName) return;
   
  try {
    const data = await apiRequest("/auth/login", {
      method: "POST",
      body: JSON.stringify({
        identifier: loginIdentifier,
        full_name: loginName,
      }),
    });

    // ✅ 1) save auth first
    localStorage.setItem("esill-token", data.token);
    localStorage.setItem("esill-user", JSON.stringify(data.user));
    window.dispatchEvent(new Event("auth-changed"));


    // ✅ 2) merge guest cart into user cart (if guest session exists)
    const sessionToken = localStorage.getItem("esill-session");
    if (sessionToken) {
      await apiRequest("/cart/merge", {
        method: "POST",
        body: JSON.stringify({ session_token: sessionToken }),
      });

      // ✅ 3) remove guest token so you don't keep loading guest cart
      localStorage.removeItem("esill-session");
      window.dispatchEvent(new Event("auth-changed"));

    }

    // ✅ 4) close + go home
    handleClose();
    navigate("/");
  } catch (err) {
  setUiError(err.message || "Something went wrong. Please try again.");
}
};



  const handleRegisterSubmit = async (e) => {
  e.preventDefault();
    setRegPhoneTouched(true);

  const err = validatePhone(regPhone);
  if (err) return;

  if (!regPhone || !regName) return;

  try {
    const data = await apiRequest("/auth/register", {
      method: "POST",
      body: JSON.stringify({
        full_name: regName,
        phone: regPhone,
        email: regEmail || null,
      }),
    });

    localStorage.setItem("esill-token", data.token);
    localStorage.setItem("esill-user", JSON.stringify(data.user));

    // 🔥 IMPORTANT: notify CartContext
    window.dispatchEvent(new Event("auth-changed"));

    handleClose();
    navigate("/");
  } catch (err) {
  setUiError(err.message || "Registration failed. Please try again.");
}
};

  const t = {
    EN: {
      welcome: "Welcome to ESIELL",
      subtitle:
        "Log in or create an account to view your orders, wishlist, and personalized offers.",
      login: "Login",
      create: "Create Account",
      continueGuest: "Continue as Guest",
      back: "Back",
      loginTitle: "Login to your account",
      loginIdentifierLabel: "Email or phone number",
      loginIdentifierPlaceholder: "Enter email or phone",
      fullNameLabel: "Full name",
      fullNamePlaceholder: "Enter your full name",
      registerTitle: "Create your account",
      phoneLabel: "Phone number",
      phonePlaceholder: "Enter your phone number",
      emailLabel: "Email (optional)",
      emailPlaceholder: "Enter your email",
      loginBtn: "Login",
      registerBtn: "Register",
    },
    AR: {
      welcome: "مرحبا بك في ESIELL",
      subtitle:
        "سجّلي دخولك أو أنشئي حساباً لعرض الطلبات والمفضلة والعروض الخاصة.",
      login: "تسجيل الدخول",
      create: "إنشاء حساب",
      continueGuest: "المتابعة كزائرة",
      back: "رجوع",
      loginTitle: "تسجيل الدخول إلى حسابك",
      loginIdentifierLabel: "البريد الإلكتروني أو رقم الهاتف",
      loginIdentifierPlaceholder: "أدخلي البريد أو رقم الهاتف",
      fullNameLabel: "الاسم الكامل",
      fullNamePlaceholder: "أدخلي اسمك الكامل",
      registerTitle: "إنشاء حساب جديد",
      phoneLabel: "رقم الهاتف",
      phonePlaceholder: "أدخلي رقم هاتفك",
      emailLabel: "البريد الإلكتروني (اختياري)",
      emailPlaceholder: "أدخلي بريدك الإلكتروني",
      loginBtn: "تسجيل الدخول",
      registerBtn: "إنشاء حساب",
    },
  }[lang];

  const loginPhoneError =
  !isEmail(loginIdentifier) ? validatePhone(loginIdentifier) : null;

const isLoginDisabled = !loginIdentifier || !loginName || !!loginPhoneError;

const regPhoneError = validatePhone(regPhone);
const isRegisterDisabled = !regPhone || !regName || !!regPhoneError;


  return (
    <div
      className={`
        fixed inset-0 z-[99999]
        flex items-center justify-center
        backdrop-blur-xl bg-black/40
        transition-opacity duration-300
        ${isOpen ? "opacity-100" : "opacity-0"}
      `}
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      {/* CLOSE BUTTON */}
      <button
        onClick={handleClose}
        className={`
          absolute top-8 text-3xl text-white opacity-80 hover:opacity-100
          transition
          ${lang === "EN" ? "right-8" : "left-8"}
        `}
      >
        ×
      </button>

      {/* LANGUAGE TOGGLE */}
      <button
        onClick={toggleLanguage}
        className={`
          absolute top-8 
          ${lang === "EN" ? "left-8" : "right-8"}
          bg-white text-black text-sm
          px-4 py-1 rounded-full shadow-md hover:bg-gray-100
        `}
      >
        {lang === "EN" ? "AR" : "EN"}
      </button>

      {/* WHITE SHARP BOX */}
      <div
        className="
          w-[88%] max-w-sm bg-white 
          shadow-2xl border border-black/15 
          px-6 py-8 
          animate-slideUp
        "
      >
        {/* HEADER (changes with mode) */}
        {mode === "choice" && (
          <>
            <h2 className="text-xl font-semibold text-center mb-2 tracking-wide">
              {t.welcome}
            </h2>

            <p className="text-sm text-center text-gray-600 mb-6 leading-relaxed">
              {t.subtitle}
            </p>

            {/* LOGIN */}
            <button
              onClick={() => {
  setUiError("");
  setMode("login");
}}

              className="
                w-full py-3 mb-3 
                bg-black text-white 
                font-semibold tracking-wide
                hover:bg-[#222] 
                transition
              "
            >
              {t.login}
            </button>

            {/* SIGNUP */}
            <button
              onClick={() => {
  setUiError("");
  setMode("register");
}}

              className="
                w-full py-3 
                border border-black 
                font-semibold tracking-wide
                hover:bg-gray-100 
                transition
              "
            >
              {t.create}
            </button>

            {/* GUEST MODE */}
            <button
              onClick={handleClose}
              className="
                w-full text-center text-[13px] mt-6
                underline text-gray-600 
                hover:text-black
              "
            >
              {t.continueGuest}
            </button>
          </>
        )}

        {mode === "login" && (
          <>
            {/* SMALL BACK LINK */}
            <button
              onClick={() => setMode("choice")}
              className="text-xs text-gray-500 mb-3 hover:text-black"
            >
              ← {t.back}
            </button>

            <h2 className="text-lg font-semibold mb-4 tracking-wide">
              {t.loginTitle}
            </h2>

            <form onSubmit={handleLoginSubmit} className="space-y-4">
              {/* EMAIL / PHONE */}
              <div>
                <label className="block text-xs font-semibold mb-1 text-[#2E3135]">
                  {t.loginIdentifierLabel}
                </label>
                <input
  type="text"
  value={loginIdentifier}
  onChange={(e) => {
    const next = e.target.value;

    // If email -> do not format
    if (isEmail(next)) {
      setLoginIdentifier(next);
      return;
    }

    // Phone -> apply rules
    setLoginIdentifier(formatPhoneInput(next));
  }}
  onBlur={() => setLoginIdTouched(true)}
  placeholder={t.loginIdentifierPlaceholder}
  className="
    w-full border border-[#E6D8D2] rounded-md
    px-3 py-2 text-sm
    focus:outline-none focus:border-black
  "
/>
{loginIdTouched && !isEmail(loginIdentifier) && validatePhone(loginIdentifier) && (
  <p className="text-xs text-red-600 mt-1">
    {validatePhone(loginIdentifier)}
  </p>
)}

              </div>

              {/* FULL NAME */}
              <div>
                <label className="block text-xs font-semibold mb-1 text-[#2E3135]">
                  {t.fullNameLabel}
                </label>
                <input
                  type="text"
                  value={loginName}
                  onChange={(e) => setLoginName(e.target.value)}
                  placeholder={t.fullNamePlaceholder}
                  className="
                    w-full border border-[#E6D8D2] rounded-md
                    px-3 py-2 text-sm
                    focus:outline-none focus:border-black
                  "
                />
              </div>

              {uiError && (
  <div className="
    bg-red-50 border border-red-200 text-red-700
    px-3 py-2 rounded-md text-sm
    animate-fadeIn
  ">
    {uiError}
  </div>
)}


              {/* BUTTON */}
              <button
                type="submit"
                disabled={isLoginDisabled}
                className={`
                  w-full py-3 mt-3 font-semibold tracking-wide
                  ${
                    isLoginDisabled
                      ? "bg-[#E6E6E6] text-[#888]"
                      : "bg-black text-white hover:bg-[#222]"
                  }
                  transition
                `}
              >
                {t.loginBtn}
              </button>
            </form>
          </>
        )}

        {mode === "register" && (
          <>
            {/* SMALL BACK LINK */}
            <button
              onClick={() => setMode("choice")}
              className="text-xs text-gray-500 mb-3 hover:text-black"
            >
              ← {t.back}
            </button>

            <h2 className="text-lg font-semibold mb-4 tracking-wide">
              {t.registerTitle}
            </h2>

            <form onSubmit={handleRegisterSubmit} className="space-y-4">
              {/* PHONE */}
              <div>
                <label className="block text-xs font-semibold mb-1 text-[#2E3135]">
                  {t.phoneLabel}
                </label>
                <input
  type="tel"
  value={regPhone}
  onChange={(e) => setRegPhone(formatPhoneInput(e.target.value))}
  onBlur={() => setRegPhoneTouched(true)}
  placeholder={t.phonePlaceholder}
  className="
    w-full border border-[#E6D8D2] rounded-md
    px-3 py-2 text-sm
    focus:outline-none focus:border-black
  "
/>
{regPhoneTouched && validatePhone(regPhone) && (
  <p className="text-xs text-red-600 mt-1">
    {validatePhone(regPhone)}
  </p>
)}

              </div>

              {/* FULL NAME */}
              <div>
                <label className="block text-xs font-semibold mb-1 text-[#2E3135]">
                  {t.fullNameLabel}
                </label>
                <input
                  type="text"
                  value={regName}
                  onChange={(e) => setRegName(e.target.value)}
                  placeholder={t.fullNamePlaceholder}
                  className="
                    w-full border border-[#E6D8D2] rounded-md
                    px-3 py-2 text-sm
                    focus:outline-none focus:border-black
                  "
                />
              </div>

              {/* EMAIL (OPTIONAL) */}
              <div>
                <label className="block text-xs font-semibold mb-1 text-[#2E3135]">
                  {t.emailLabel}
                </label>
                <input
                  type="email"
                  value={regEmail}
                  onChange={(e) => setRegEmail(e.target.value)}
                  placeholder={t.emailPlaceholder}
                  className="
                    w-full border border-[#E6D8D2] rounded-md
                    px-3 py-2 text-sm
                    focus:outline-none focus:border-black
                  "
                />
              </div>

              {uiError && (
  <div className="
    bg-red-50 border border-red-200 text-red-700
    px-3 py-2 rounded-md text-sm
    animate-fadeIn
  ">
    {uiError}
  </div>
)}


              {/* BUTTON */}
              <button
                type="submit"
                disabled={isRegisterDisabled}
                className={`
                  w-full py-3 mt-3 font-semibold tracking-wide
                  ${
                    isRegisterDisabled
                      ? "bg-[#E6E6E6] text-[#888]"
                      : "bg-black text-white hover:bg-[#222]"
                  }
                  transition
                `}
              >
                {t.registerBtn}
              </button>
            </form>
          </>
        )}
      </div>

      {/* ANIMATION */}
      <style>{`
        @keyframes fadeIn {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeIn {
  animation: fadeIn 0.25s ease-out;
}

        @keyframes slideUp {
          from { transform: translateY(40px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slideUp {
          animation: slideUp 0.45s ease-out;
        }
      `}</style>
    </div>
  );
}

export default EntryModal;
